/** QuickRouter — Couches et impédance (propriétés de la carte) : nombre de couches, empilage
 *  JLCPCB, rôle des couches internes, isolement entre domaines, calculateur d'impédance. */
import { useState } from 'react';
import { useProjectStore } from '../store/useProjectStore.js';
import { copperLayers, layerRole, isOuter, LAYER_COLORS, LAYER_LABELS } from '../../shared/layers.js';
import { stackupOf, stackupsFor, synthesize, impedance } from '../../shared/stackup.js';
import NumField from './NumField.jsx';

export default function LayersPanel() {
  const project = useProjectStore((s) => s.project);
  const mutate = useProjectStore((s) => s.mutate);
  const setStatus = useProjectStore((s) => s.setStatus);
  const ro = project.role === 'viewer';
  const b = project.board;
  const st = stackupOf(b);
  const cu = copperLayers(b);
  const [calc, setCalc] = useState({ layer: 'F', target: 90, diff: true });

  const setLayers = (n) => {
    const keep = new Set(copperLayers({ layers: n }));
    const lost = project.tracks.filter((t) => !keep.has(t.layer)).length + project.zones.filter((z) => !keep.has(z.layer)).length;
    if (lost) { setStatus(`⚠ ${lost} piste(s) ou zone(s) sur des couches qui disparaîtraient : déplacez-les d'abord`); return; }
    mutate((p) => { p.board.layers = n; delete p.board.stackup; delete p.board.roles; });
  };

  let result = null;
  try {
    if (cu.includes(calc.layer)) result = synthesize(b, calc.layer, calc.target, { diff: calc.diff });
  } catch { result = null; }

  return (
    <>
      <h3>Couches et empilage</h3>
      <div className="form-grid">
        <label>Couches cuivre</label>
        <div className="seg">
          {[2, 4, 6].map((n) => <button key={n} disabled={ro} className={b.layers === n ? 'active' : ''} onClick={() => setLayers(n)}>{n}</button>)}
        </div>
        <label>Empilage</label>
        <select disabled={ro} value={st.id} onChange={(e) => mutate((p) => { p.board.stackup = e.target.value; })}>
          {stackupsFor(b.layers).map((s) => <option key={s.id} value={s.id}>{s.id}</option>)}
        </select>
        <label>Isolement domaines</label>
        <NumField disabled={ro} value={project.rules.isolation} onCommit={(v) => v >= 0 && mutate((p) => { p.rules.isolation = v; })} title="Distance minimale entre nets de domaines isolés différents (mm)" />
      </div>
      <div className="stack">
        {st.stack.map((x, k) => (x.type === 'copper' ? (
          <div key={k} className="stack-cu" style={{ borderLeftColor: LAYER_COLORS[x.name] }}>
            <span className="mono">{x.name}</span>
            <span className="grow muted">{x.t} mm</span>
            {isOuter(x.name) ? <span className="muted">signal</span> : (
              <select disabled={ro} value={layerRole(b, x.name)} onChange={(e) => mutate((p) => { p.board.roles = { ...(p.board.roles ?? {}), [x.name]: e.target.value }; })}>
                <option value="signal">signal</option><option value="plane">plan</option>
              </select>
            )}
          </div>
        ) : (
          <div key={k} className="stack-di"><span className="muted">{x.material} · {x.t} mm · εr {x.er}</span></div>
        )))}
      </div>
      <p className="muted small">Valeurs d'empilage publiées par JLCPCB, à vérifier sur son calculateur ; saisir la référence d'empilage à la commande.</p>

      <h3>Calculateur d'impédance</h3>
      <div className="form-grid">
        <label>Couche</label>
        <select value={calc.layer} onChange={(e) => setCalc({ ...calc, layer: e.target.value })}>
          {cu.map((L) => <option key={L} value={L}>{LAYER_LABELS[L]}</option>)}
        </select>
        <label>Impédance (Ω)</label>
        <NumField value={calc.target} onCommit={(v) => v > 0 && setCalc({ ...calc, target: v })} />
        <label>Paire</label>
        <input type="checkbox" checked={calc.diff} onChange={(e) => setCalc({ ...calc, diff: e.target.checked })} />
      </div>
      {result && (
        <div className="info-box">
          {result.geometry.kind === 'microstrip' ? 'Microstrip' : 'Stripline'} : largeur <b>{result.w} mm</b>{result.s != null && <> · écart <b>{result.s} mm</b></>} → {result.z} Ω
          {result.s == null && <> (paire au même pas : {Math.round(impedance(b, calc.layer, result.w, result.w) * 10) / 10} Ω diff.)</>}
          <div className="muted small">Précision ±10 % ; le vernis abaisse de 1 à 3 Ω en surface.</div>
        </div>
      )}
    </>
  );
}
